import {
  GLM_EAST_SWITCH_YMD,
  GLM_G17_START_YMD,
  GLM_WEST_SWITCH_YMD,
  parseGlmObjectName,
  utcDayOfYear,
  ymdNumber,
} from "./glm.js";

export const GOES_START_YMD = 20171219;
export const GOES_MATCH_MS = 5 * 60 * 1000;
const CROP_PAD = 1.15;
const MAX_CROP = 640;
// Viewport crops (national view) are decimated to at most this many cells a side.
export const VIEW_MAX_SIZE = 1536;
// Auto picks a Meso sector when it holds this much around the station.
export const MESO_FIT_KM = 250;
// Crop edges land on a coarse index grid so the same scan and area give the
// same window, and so the same cache key.
export const CROP_SNAP = 32;

const EAST_BEFORE = { id: "G16", bucket: "gcp-public-data-goes-16" };
const EAST_AFTER = { id: "G19", bucket: "gcp-public-data-goes-19" };
const WEST_G17 = { id: "G17", bucket: "gcp-public-data-goes-17" };
const WEST_G18 = { id: "G18", bucket: "gcp-public-data-goes-18" };

export function goesAvailableOn(date) {
  const ymd = ymdNumber(date);
  return ymd != null && ymd >= GOES_START_YMD;
}

function eastSatellite(ymd) {
  return ymd >= GLM_EAST_SWITCH_YMD ? EAST_AFTER : EAST_BEFORE;
}

function westSatellite(ymd) {
  if (ymd >= GLM_WEST_SWITCH_YMD) return WEST_G18;
  if (ymd >= GLM_G17_START_YMD) return WEST_G17;
  return null;
}

// Where each satellite's CONUS sector safely holds a crop center. East's covers
// the lower 48. West's (PACUS) is a trapezoid, 184-90°W along 53.5°N but only
// 162-112°W along 14.8°N, so its box stays inside that. A crop that still runs
// off the grid falls back to Full Disk.
const CONUS_BOX = {
  east: { south: 14, north: 52, west: -128, east: -52 },
  west: { south: 15, north: 53, west: -165, east: -105 },
};

function inBox(lat, lon, box) {
  return lat >= box.south && lat <= box.north && lon >= box.west && lon <= box.east;
}

export function goesSectorForStation(lat, lon, satellite = "east") {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return "F";
  return inBox(lat, lon, CONUS_BOX[satellite] || CONUS_BOX.east) ? "C" : "F";
}

// Which satellite a viewer asked for. "auto" uses West west of 110°W and over
// the Pacific, East elsewhere, and the other satellite's CONUS sector when the
// nearer one's does not hold the area.
export const GOES_SATELLITES = Object.freeze([
  { id: "auto", label: "Auto" },
  { id: "east", label: "East" },
  { id: "west", label: "West" },
]);

export function normalizeSatChoice(value) {
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "e") return "east";
  if (text === "w") return "west";
  return GOES_SATELLITES.some((item) => item.id === text) ? text : "auto";
}

// A forced West before GOES-17 (Feb 2019) falls back to East.
export function selectGoesSource(date, lat, lon, options = {}) {
  const ymd = ymdNumber(date);
  if (ymd == null || ymd < GOES_START_YMD) return null;
  const choice = normalizeSatChoice(options.satellite);
  const east = eastSatellite(ymd);
  const west = westSatellite(ymd);
  let sides;
  if (choice === "west" && west) sides = ["west"];
  else if (choice === "east" || !west) sides = ["east"];
  else sides = lon > 0 || lon <= -110 ? ["west", "east"] : ["east", "west"];
  const sats = { east, west };
  if (!options.forceFullDisk) {
    const side = sides.find((s) => goesSectorForStation(lat, lon, s) === "C");
    if (side) return { sector: "C", product: "ABI-L2-MCMIPC", sat: sats[side] };
  }
  return { sector: "F", product: "ABI-L2-MCMIPF", sat: sats[sides[0]] };
}

// The satellites whose Meso sectors may hold the area, best first. Meso
// sectors are placed per satellite, so "auto" checks both.
export function goesMesoSatellites(date, lat, lon, satellite) {
  const preferred = selectGoesSource(date, lat, lon, { forceFullDisk: true, satellite });
  if (!preferred) return [];
  if (normalizeSatChoice(satellite) !== "auto") return [preferred.sat];
  const ymd = ymdNumber(date);
  const other = preferred.sat === westSatellite(ymd) ? eastSatellite(ymd) : westSatellite(ymd);
  return other ? [preferred.sat, other] : [preferred.sat];
}

// Sector choices a viewer can force. "auto" picks Meso, then CONUS, then Full Disk.
export const GOES_SECTORS = Object.freeze([
  { id: "auto", label: "Auto" },
  { id: "m", label: "Meso" },
  { id: "c", label: "CONUS" },
  { id: "f", label: "Full disk" },
]);

export function normalizeSatSector(value) {
  const text = String(value ?? "").trim().toLowerCase();
  return GOES_SECTORS.some((item) => item.id === text) ? text : "auto";
}

// Time between scans for a sector id as it appears on a frame ("M1", "C", "F")
// or as a viewer choice ("m", "c", "f").
export function goesCadenceMs(sector) {
  const id = String(sector || "").trim().toUpperCase().charAt(0);
  if (id === "M") return 60 * 1000;
  if (id === "F") return 10 * 60 * 1000;
  return 5 * 60 * 1000;
}

// Every hour directory that overlaps [startMs, endMs].
export function goesHourPrefixesBetween(startMs, endMs, product) {
  const prefixes = [];
  const hour = 3600000;
  for (let ms = Math.floor(startMs / hour) * hour; ms <= endMs; ms += hour) {
    const dt = new Date(ms);
    const doy = String(utcDayOfYear(ms)).padStart(3, "0");
    const hh = String(dt.getUTCHours()).padStart(2, "0");
    prefixes.push(`${product}/${dt.getUTCFullYear()}/${doy}/${hh}/`);
  }
  return prefixes;
}

export function goesHourPrefixes(centerMs, product) {
  const prefixes = [];
  const seen = new Set();
  for (const delta of [-3600000, 0, 3600000]) {
    const ms = centerMs + delta;
    const dt = new Date(ms);
    const year = dt.getUTCFullYear();
    const doy = String(utcDayOfYear(ms)).padStart(3, "0");
    const hour = String(dt.getUTCHours()).padStart(2, "0");
    const prefix = `${product}/${year}/${doy}/${hour}/`;
    if (seen.has(prefix)) continue;
    seen.add(prefix);
    prefixes.push(prefix);
  }
  return prefixes;
}

export function pickGoesFile(files, centerMs, maxDeltaMs = GOES_MATCH_MS) {
  let best = null;
  let bestDist = Infinity;
  for (const file of files || []) {
    const meta = parseGlmObjectName(file.name);
    if (!meta || !Number.isFinite(meta.startMs) || !Number.isFinite(meta.endMs)) continue;
    const mid = (meta.startMs + meta.endMs) / 2;
    const dist = Math.abs(mid - centerMs);
    if (dist < bestDist) {
      best = { ...file, startMs: meta.startMs, endMs: meta.endMs };
      bestDist = dist;
    }
  }
  if (!best || bestDist > maxDeltaMs) return null;
  return best;
}

function satelliteRange(proj) {
  return proj.height + proj.req;
}

const MERCATOR_MAX_LAT = 85.0511287798;

export function webMercatorWorldSize(zoom) {
  return 256 * (2 ** zoom);
}

export function latLonToMercatorPixel(lat, lon, worldSize) {
  const clamped = Math.max(-MERCATOR_MAX_LAT, Math.min(MERCATOR_MAX_LAT, lat));
  const sin = Math.sin((clamped * Math.PI) / 180);
  return {
    x: ((lon / 360) + 0.5) * worldSize,
    y: (0.5 - (Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI))) * worldSize,
  };
}

export function mercatorPixelToLatLon(x, y, worldSize) {
  const nx = x / worldSize;
  const ny = y / worldSize;
  const lon = (nx * 360) - 180;
  const merc = Math.PI * (1 - (2 * ny));
  const sinh = (Math.exp(merc) - Math.exp(-merc)) / 2;
  return {
    lat: (Math.atan(sinh) * 180) / Math.PI,
    lon,
  };
}

export function scanToLatLon(x, y, proj) {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const H = satelliteRange(proj);
  const { req, rpol, lon0 } = proj;
  const cosX = Math.cos(x);
  const sinX = Math.sin(x);
  const cosY = Math.cos(y);
  const sinY = Math.sin(y);
  const a = (sinX * sinX) + (cosX * cosX) * ((cosY * cosY) + ((req * req) / (rpol * rpol)) * (sinY * sinY));
  const b = -2 * H * cosX * cosY;
  const c = (H * H) - (req * req);
  const disc = (b * b) - (4 * a * c);
  if (!(disc >= 0) || a === 0) return null;
  const rs = (-b - Math.sqrt(disc)) / (2 * a);
  const sx = rs * cosX * cosY;
  const sy = -rs * sinX;
  const sz = rs * cosX * sinY;
  const lat = Math.atan(((req * req) / (rpol * rpol)) * (sz / Math.sqrt(((H - sx) * (H - sx)) + (sy * sy))));
  const lon = (lon0 * Math.PI / 180) - Math.atan2(sy, H - sx);
  return { lat: lat * 180 / Math.PI, lon: lon * 180 / Math.PI };
}

export function latLonToScan(latDeg, lonDeg, proj) {
  if (!Number.isFinite(latDeg) || !Number.isFinite(lonDeg)) return null;
  const H = satelliteRange(proj);
  const { req, rpol, lon0 } = proj;
  const lat = latDeg * Math.PI / 180;
  const lon = lonDeg * Math.PI / 180;
  const lambda0 = lon0 * Math.PI / 180;
  const phiC = Math.atan(((rpol * rpol) / (req * req)) * Math.tan(lat));
  const rc = rpol / Math.sqrt(1 - (((req * req) - (rpol * rpol)) / (req * req)) * (Math.cos(phiC) ** 2));
  const sx = H - rc * Math.cos(phiC) * Math.cos(lon - lambda0);
  const sy = -rc * Math.cos(phiC) * Math.sin(lon - lambda0);
  const sz = rc * Math.sin(phiC);
  if (H * (H - sx) < (sy * sy) + ((req * req) / (rpol * rpol)) * (sz * sz)) return null;
  const norm = Math.sqrt((sx * sx) + (sy * sy) + (sz * sz));
  if (!(norm > 0) || sx === 0) return null;
  return {
    x: Math.asin(-sy / norm),
    y: Math.atan(sz / sx),
  };
}

export function indexToScan(ix, iy, proj) {
  return {
    x: proj.xAngle0 + (ix + 0.5) * proj.xAngleStep,
    y: proj.yAngle0 + (iy + 0.5) * proj.yAngleStep,
  };
}

export function indexToLatLon(ix, iy, proj) {
  const scan = indexToScan(ix, iy, proj);
  return scanToLatLon(scan.x, scan.y, proj);
}

export function latLonToIndex(lat, lon, proj) {
  const scan = latLonToScan(lat, lon, proj);
  if (!scan || !proj.xAngleStep || !proj.yAngleStep) return null;
  return {
    ix: ((scan.x - proj.xAngle0) / proj.xAngleStep) - 0.5,
    iy: ((scan.y - proj.yAngle0) / proj.yAngleStep) - 0.5,
  };
}

function shrinkSpan(min, max, limit) {
  if (max - min <= limit) return [min, max];
  const mid = (min + max) / 2;
  return [mid - limit / 2, mid + limit / 2];
}

function radiusPoints(lat, lon, radiusKm) {
  const latPad = radiusKm / 111;
  const cosLat = Math.cos((lat * Math.PI) / 180);
  const lonPad = latPad / Math.max(0.2, Math.abs(cosLat));
  return [
    [lat + latPad, lon],
    [lat - latPad, lon],
    [lat, lon + lonPad],
    [lat, lon - lonPad],
  ];
}

// Points along a {north, south, east, west} box, so a curved GOES grid still
// sees the whole viewport.
function boundsPoints({ north, south, east, west }, steps = 8) {
  const points = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const la = south + (north - south) * t;
    const lo = west + (east - west) * t;
    points.push([north, lo], [south, lo], [la, west], [la, east]);
  }
  return points;
}

function insideGrid(idx, proj) {
  return idx && idx.ix >= 0 && idx.iy >= 0 && idx.ix < proj.nx && idx.iy < proj.ny;
}

function snapWindow(minX, maxX, minY, maxY, proj, stride) {
  const unit = CROP_SNAP * stride;
  const x0 = Math.max(0, Math.floor((Math.floor(minX) - 1) / unit) * unit);
  const x1 = Math.min(proj.nx, Math.ceil((Math.ceil(maxX) + 2) / unit) * unit);
  const y0 = Math.max(0, Math.floor((Math.floor(minY) - 1) / unit) * unit);
  const y1 = Math.min(proj.ny, Math.ceil((Math.ceil(maxY) + 2) / unit) * unit);
  if (x1 - x0 < 2 * stride || y1 - y0 < 2 * stride) return null;
  return { x0, x1, y0, y1, stride };
}

// Power-of-two decimation, so small zoom changes keep the same window.
export function cropStride(span, maxSize) {
  let stride = 1;
  while (span / stride > maxSize) stride *= 2;
  return stride;
}

// The grid window to read. With `bounds` it covers that box, decimated to fit
// `maxSize` cells; otherwise it covers `rangeKm` around lat/lon at full
// resolution, capped at MAX_CROP.
export function cropIndexWindow({ lat, lon, rangeKm, proj, bounds, maxSize = VIEW_MAX_SIZE }) {
  if (!(proj.nx > 1) || !(proj.ny > 1)) return null;
  if (bounds) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [la, lo] of boundsPoints(bounds)) {
      const idx = latLonToIndex(la, lo, proj);
      if (!idx) continue;
      minX = Math.min(minX, idx.ix);
      maxX = Math.max(maxX, idx.ix);
      minY = Math.min(minY, idx.iy);
      maxY = Math.max(maxY, idx.iy);
    }
    if (!Number.isFinite(minX)) return null;
    minX = Math.max(0, minX);
    minY = Math.max(0, minY);
    maxX = Math.min(proj.nx - 1, maxX);
    maxY = Math.min(proj.ny - 1, maxY);
    if (maxX <= minX || maxY <= minY) return null;
    const stride = cropStride(Math.max(maxX - minX, maxY - minY), maxSize);
    return snapWindow(minX, maxX, minY, maxY, proj, stride);
  }
  const center = latLonToIndex(lat, lon, proj);
  if (!insideGrid(center, proj)) return null;
  const range = Number(rangeKm) > 0 ? Number(rangeKm) : 300;
  let minX = center.ix;
  let maxX = center.ix;
  let minY = center.iy;
  let maxY = center.iy;
  for (const [la, lo] of radiusPoints(lat, lon, range * CROP_PAD)) {
    const idx = latLonToIndex(la, lo, proj);
    if (!idx) continue;
    minX = Math.min(minX, idx.ix);
    maxX = Math.max(maxX, idx.ix);
    minY = Math.min(minY, idx.iy);
    maxY = Math.max(maxY, idx.iy);
  }
  [minX, maxX] = shrinkSpan(minX, maxX, MAX_CROP);
  [minY, maxY] = shrinkSpan(minY, maxY, MAX_CROP);
  return snapWindow(minX, maxX, minY, maxY, proj, 1);
}

// Whether a sector's grid holds the area: `radiusKm` around lat/lon, or the
// whole `bounds` box. Meso sectors are only used when they do.
export function gridCovers(proj, { lat, lon, radiusKm, bounds }) {
  const points = bounds ? boundsPoints(bounds) : [[lat, lon], ...radiusPoints(lat, lon, radiusKm)];
  return points.every(([la, lo]) => insideGrid(latLonToIndex(la, lo, proj), proj));
}

export function sameGrid(a, b) {
  if (!a || !b) return false;
  return ["lon0", "xAngle0", "yAngle0", "xAngleStep", "yAngleStep", "nx", "ny"]
    .every((key) => Math.abs(a[key] - b[key]) < 1e-9);
}

// "M1" / "M2" from an MCMIPM object name.
export function mesoSectorOf(name) {
  const match = /MCMIPM([12])-/.exec(String(name || ""));
  return match ? `M${match[1]}` : null;
}
